/**
 * AVBP — PayPal Tier B (USD) thin portal seam.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The minimal client wrapper over the `paypalCreateOrder` Cloud Function.
 *  Staff click "Generate PayPal link (USD)" on a USD money gate → this calls
 *  the CF → returns the PayPal `approve` link to copy + send to the client.
 *
 *  PayPal is USD-ONLY (no domestic India rail). The amount sent IS the
 *  resolved gate balance (one money path — the gate is the authority). The
 *  actual gate RELEASE happens server-side when the client pays: the PayPal
 *  capture/webhook clears the gate via the same `clearGatePaid` path
 *  markPaidManual uses. Nothing here touches gateState.
 *
 *  This is intentionally thin — the full multi-method "Payment Details
 *  Sender" composer (UPI/Bank/Razorpay/PayPal multi-select, QR templates,
 *  WhatsApp/email send) is a SEPARATE future build (see
 *  PAYMENT_DETAILS_SENDER_SPEC.html). Do not grow this into that here.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface PaypalOrderInput {
  /** The project the gate belongs to. */
  projectId: string;
  /** The money gate this order pays (e.g. 'advance', 'stage1'). */
  gateId: string;
  /** The resolved gate balance, in USD (the gate's own currency — no FX). */
  amountUSD: number;
}

export interface PaypalOrderResult {
  ok: boolean;
  /** PayPal order id. */
  orderId: string;
  /** The `approve` link the client opens to pay — copy & send this. */
  approveUrl: string;
  /** The encoded custom_id (projectId|gateId|amountUSD) — for reference. */
  customId: string;
}

/**
 * Create a PayPal USD CAPTURE order for a gate's balance and return the
 * approve link for staff to copy. Throws on validation/permission/PayPal
 * error (the CF surfaces a clear message).
 */
export async function createPaypalLink(
  input: PaypalOrderInput,
): Promise<PaypalOrderResult> {
  if (!input.projectId) throw new Error('No project selected.');
  if (!input.gateId) throw new Error('No money gate selected.');
  if (!(input.amountUSD > 0)) {
    throw new Error('Enter a positive USD amount for the PayPal link.');
  }
  const fn = httpsCallable<PaypalOrderInput, PaypalOrderResult>(
    functions,
    'paypalCreateOrder',
  );
  const res = await fn({
    projectId: input.projectId,
    gateId: input.gateId,
    amountUSD: input.amountUSD,
  });
  return res.data;
}
